/**
 * Resolutions / refund method column: icons, no-offers warning, and resolution label (copied from legacy list).
 */
import { observer } from "mobx-react";
import { useMemo } from "react";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import { FONT_COLOR } from "../../../../../components/fonts/font-props";
import IconIsReturnGoResolution from "../../../../../components/icons/icon-return-go-resolution/icon-return-go-resolution";
import IconReturnNotRefundExchange from "../../../../../components/icons/icon-return-not-refund-exchange/icon-return-not-refund-exchange";
import IconWarning from "../../../../../components/icons/icon-warning/icon-warning";
import { TOOLTIP_VARIANTS, Tooltip } from "../../../../../components/tooltip/tooltip";
import refundIdSrc from "../../../../../images/refundid.svg";
import reveniSrc from "../../../../../images/reveni.svg";
import riseSrc from "../../../../../images/rise.svg";
import { RMA_PROVIDERS } from "../../../../../services/api/rma-list-service";
import RmaListItemStore from "../../../rma-list-page/stores/rma-item-store";
import "./table-column-resolutions.scss";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";

export interface ITableColumnResolutionsProps {
    rma: RmaListItemStore;
}

const getProviderImage = (provider: RMA_PROVIDERS) => {
    switch (provider) {
        case RMA_PROVIDERS.REFUND_ID:
            return <img src={refundIdSrc} alt={RMA_PROVIDERS.REFUND_ID} />;
        case RMA_PROVIDERS.REVENI:
            return <img src={reveniSrc} alt={RMA_PROVIDERS.REFUND_ID} />;
        case RMA_PROVIDERS.RISE:
            return <img src={riseSrc} alt={RMA_PROVIDERS.RISE} />;
    }
};

const TableColumnResolutions: React.FC<ITableColumnResolutionsProps> = observer(({ rma }) => {
    const scope = "dashboard-returns-page";
    const { getTranslation } = useDashboardTranslation();
    const noOffersIcon = useMemo(() => {
        if (!rma.noOffers) {
            return null;
        }
        if (rma.noOffers > 0) {
            const title =
                rma.noOffers === rma.amount ? getTranslation("NO_OFFERS_NOTE_1", scope) : getTranslation("NO_OFFERS_NOTE_2", scope);
            return (
                <Tooltip
                    variant={TOOLTIP_VARIANTS.WARNING}
                    content={
                        <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                            <div className="table-column-resolutions__nooffer-tooltip">{title}</div>
                        </FontBody2>
                    }
                >
                    <IconWarning color="var(--color-semantic-warning)" />
                </Tooltip>
            );
        }
    }, [getTranslation, rma.noOffers, rma.amount]);
    const icon = useMemo(() => {
        if (rma.isRreturngoResolution) {
            return <IconIsReturnGoResolution />;
        }
        if (rma.hasExchange) {
            return <IconReturnNotRefundExchange />;
        }
        if (rma.refundProvider) {
            return getProviderImage(rma.refundProvider);
        }
        if (rma.creditIntegrationProvider) {
            return getProviderImage(rma.creditIntegrationProvider);
        }
        return null;
    }, [rma.refundProvider, rma.creditIntegrationProvider, rma.hasExchange, rma.isRreturngoResolution]);
    const showResolutionName = !rma.noOffers || rma.noOffers !== rma.amount;
    return (
        <div className="table-column-resolutions">
            {noOffersIcon}
            {icon}
            <FontBody1>{showResolutionName && rma.resolutionName}</FontBody1>
        </div>
    );
});

export default TableColumnResolutions;
